/**
 * Workflow Canvas (P2.1-S5 polish).
 *
 * Thêm:
 * - <MiniMap /> góc dưới phải cho navigation workflow lớn.
 * - defaultEdgeOptions: smoothstep + animated cho dòng chảy mềm hơn.
 * - Controls position bottom-left + cleaner styling.
 */
import * as React from "react";
import ReactFlow, {
  Background,
  type Connection,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  MiniMap,
  type Node,
  type NodeChange,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";

import type { NodeType } from "@/api/workflows";
import { NODE_TYPES } from "@/components/workflow/nodes/WorkflowNode";
import { useWorkflowDesignerStore } from "@/stores/workflowDesigner";

/** Smoothstep + arrow marker — applies tới mọi edge mới. */
const DEFAULT_EDGE_OPTIONS = {
  type: "smoothstep",
  animated: true,
  style: { strokeWidth: 1.5 },
  markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
};

/** MiniMap node color theo type — dùng key matching workflow node types. */
function miniMapNodeColor(node: Node): string {
  const t = (node.data as { nodeType?: string } | undefined)?.nodeType;
  switch (t) {
    case "trigger":
      return "#10b981";
    case "llm":
      return "#8b5cf6";
    case "tool":
      return "#3b82f6";
    case "form":
      return "#06b6d4";
    case "branch":
      return "#f59e0b";
    case "parallel":
      return "#eab308";
    case "human":
      return "#f97316";
    case "notify":
      return "#ec4899";
    case "delay":
      return "#64748b";
    case "subflow":
      return "#a855f7";
    case "end":
      return "#ef4444";
    default:
      return "#94a3b8";
  }
}

export function Canvas(): JSX.Element {
  const flowNodes = useWorkflowDesignerStore((s) => s.flowNodes);
  const flowEdges = useWorkflowDesignerStore((s) => s.flowEdges);
  const setNodes = useWorkflowDesignerStore((s) => s.setNodes);
  const setEdges = useWorkflowDesignerStore((s) => s.setEdges);
  const addEdge = useWorkflowDesignerStore((s) => s.addEdge);
  const addNode = useWorkflowDesignerStore((s) => s.addNode);
  const selectNode = useWorkflowDesignerStore((s) => s.selectNode);

  const handleNodesChange = React.useCallback(
    (changes: NodeChange[]) => {
      const next = applyNodeChanges(changes, flowNodes as unknown as Node[]);
      setNodes(next as unknown as typeof flowNodes);
    },
    [flowNodes, setNodes],
  );

  const handleEdgesChange = React.useCallback(
    (changes: EdgeChange[]) => {
      const next = applyEdgeChanges(changes, flowEdges as unknown as Edge[]);
      setEdges(next as unknown as typeof flowEdges);
    },
    [flowEdges, setEdges],
  );

  const handleConnect = React.useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target) return;
      const sourceNode = flowNodes.find((n) => n.id === conn.source);
      // Branch node có 2 source handles "true"/"false"
      const isBranchSource = sourceNode?.data.nodeType === "branch";
      const condition_label = isBranchSource ? conn.sourceHandle ?? null : null;
      const id = `e_${conn.source}_${conn.target}_${Date.now()}`;
      addEdge({
        id,
        source: conn.source,
        target: conn.target,
        label: condition_label,
        data: { condition_label },
      });
    },
    [addEdge, flowNodes],
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/x-workflow-node") as NodeType;
      if (!type) return;
      const bounds = (e.currentTarget as HTMLElement).getBoundingClientRect();
      addNode(type, {
        x: e.clientX - bounds.left,
        y: e.clientY - bounds.top,
      });
    },
    [addNode],
  );

  return (
    <main
      className="flex-1 bg-muted/30 relative"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={flowNodes as unknown as Node[]}
        edges={flowEdges as unknown as Edge[]}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeClick={(_, node) => selectNode(node.id)}
        onPaneClick={() => selectNode(null)}
        nodeTypes={NODE_TYPES}
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} />
        <Controls
          position="bottom-left"
          className="!bg-background !border !shadow-sm !rounded-md"
        />
        <MiniMap
          position="bottom-right"
          pannable
          zoomable
          nodeColor={miniMapNodeColor}
          nodeStrokeWidth={2}
          className="!bg-background !border !shadow-sm !rounded-md"
        />
      </ReactFlow>
    </main>
  );
}
